// One person's own page: who they are to the plan, what a principal can do about that, and
// what Mor has been told about them.
//
// The header says which of four things the person is: not a collaborator on the plan, a
// collaborator not yet invited, invited and not yet joined, or joined. A principal gets the
// hand actions for that state (rename, email, whether they collaborate, delete) and the
// Topics box, where each topic is added or taken out. Nothing here invites anyone: whether an
// invitation follows is the topic's own step. The notes are the decision page's two
// sections, about this person. The server decides what the reader may see and what a change
// may do; this page hides only what the client already knows the server would refuse, and
// shows any other refusal as the server's own sentence.

import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { ApiFailure, type ApiClient } from '../api/client.js';
import type {
  NoteView,
  PersonNotes,
  PersonTopic,
  RecordChange,
} from '../api/contract.js';
import { InlineForm, RemoveConfirm } from '../components/RowEdit.js';
import { hueOf, initialsOf } from '../components/PersonRow.js';
import { firstName, relationLabel } from '../format.js';
import { viewerIsPrincipal } from '../plan/snapshot.js';
import type { PlanState } from '../plan/usePlan.js';

export type PersonPageProps = {
  state: PlanState;
  client: ApiClient;
  planId: string;
  rosterEntryId: string;
};

/** "March 4", with the year added when it is not this one. Always a date, never a time. */
function noteDate(iso: string | null): string {
  if (iso === null) {
    return '';
  }
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) {
    return '';
  }
  const sameYear = at.getFullYear() === new Date().getFullYear();
  return at.toLocaleDateString(undefined, {
    month: 'long',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
}

const TOPIC_STATE_WORD: Record<PersonTopic['state'], string> = {
  not_part: '',
  not_started: 'Not started',
  not_yet_invited: 'Not yet invited',
  invited: 'Invited',
  joined: 'Joined',
};

type Editing = 'name' | 'email' | 'delete' | null;

export function PersonPage({
  state,
  client,
  planId,
  rosterEntryId,
}: PersonPageProps): JSX.Element | null {
  const snapshot = state.snapshot;
  const navigate = useNavigate();
  const [notes, setNotes] = useState<PersonNotes | null>(null);
  const [missing, setMissing] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [editing, setEditing] = useState<Editing>(null);
  const [busy, setBusy] = useState(false);
  // The topic list after the latest change the server accepted, kept until a reload
  // succeeds; the next press builds on it, not on a response that may be older.
  const [pendingTopics, setPendingTopics] = useState<readonly string[] | null>(null);
  // True from an accepted change until the reload that follows it has settled.
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    let current = true;
    client.personNotes(planId, rosterEntryId).then(
      (loaded) => {
        if (current) {
          setNotes(loaded);
          setPendingTopics(null);
          setRefreshing(false);
          setMissing(false);
        }
      },
      (error: unknown) => {
        if (!current) {
          return;
        }
        setRefreshing(false);
        if (error instanceof ApiFailure && error.status === 404) {
          setMissing(true);
        } else {
          setProblem('That didn’t load. Try again.');
        }
      },
    );
    return () => {
      current = false;
    };
  }, [client, planId, rosterEntryId, snapshot]);

  if (snapshot === null) {
    return null;
  }
  if (missing) {
    return (
      <section className="page">
        <p className="screen__problem">This isn’t available to you.</p>
      </section>
    );
  }
  if (notes === null) {
    return problem === null ? null : (
      <section className="page">
        <p className="screen__problem">{problem}</p>
      </section>
    );
  }

  const { person } = notes;
  const canEdit = viewerIsPrincipal(snapshot) && !person.self;
  const shownName = person.self
    ? person.name.trim() === ''
      ? 'You'
      : `${person.name} — you`
    : person.name;
  const first = person.self ? 'you' : firstName(person.name);

  /** Sends one change. Resolves true when it was accepted. */
  const send = async (change: RecordChange): Promise<boolean> => {
    setBusy(true);
    setProblem(null);
    try {
      await client.changeRecord(planId, change);
      state.refetch();
      return true;
    } catch (error) {
      setProblem(
        error instanceof ApiFailure && error.code === 'refused'
          ? error.message
          : 'That didn’t save. Try again.',
      );
      return false;
    } finally {
      setBusy(false);
    }
  };

  const joined = person.joined;
  // An invitation into a topic counts too: it can go out while the plan invitation is
  // skipped for lack of an email address.
  const invited =
    !joined &&
    (person.invited ||
      notes.topics.some(
        (topic) => topic.state === 'invited' || topic.state === 'joined',
      ));
  const date = (iso: string | null): string => {
    const text = noteDate(iso);
    return text === '' ? '' : ` on ${text}`;
  };
  const stateLine = person.self
    ? `Joined this plan${date(person.joinedAt)}.`
    : !person.takesPart
      ? `${first} will not be invited to collaborate on this plan.`
      : joined
        ? `A collaborator on this plan, joined${date(person.joinedAt)}.`
        : invited
          ? `A collaborator on this plan, invited${date(person.invitedAt)}. Hasn’t joined yet.`
          : 'A collaborator on this plan, not yet invited.';
  const emailLine =
    !person.self && person.takesPart && viewerIsPrincipal(snapshot)
      ? person.email === null || person.email === ''
        ? 'No email address on file.'
        : person.email
      : null;

  const canRename = canEdit && !joined;
  const canEmail = canEdit && person.takesPart && !joined;
  const hasEmail = person.email !== null && person.email !== '';
  const canToggle =
    canEdit && !(person.takesPart && (person.sameSay || invited || joined));
  const canDelete = canEdit && !joined && !invited;
  const closeForm = (): void => {
    setProblem(null);
    setEditing(null);
  };
  const openForm = (next: Editing): void => {
    setProblem(null);
    setEditing(next);
  };

  const changeTopics = (topic: PersonTopic): void => {
    const held = pendingTopics ?? person.topics;
    const next =
      topic.state === 'not_part'
        ? [...held.filter((key) => key !== topic.key), topic.key]
        : held.filter((key) => key !== topic.key);
    void send({ kind: 'set_person_topics', person: person.id, topics: next }).then(
      (saved) => {
        if (saved) {
          setPendingTopics(next);
          setRefreshing(true);
        }
      },
    );
  };

  /** A word or a button, never both: Add or Remove where a change is allowed. */
  const topicEnd = (topic: PersonTopic): JSX.Element => {
    const action =
      topic.started && topic.state === 'not_part'
        ? 'Add'
        : topic.started && topic.state === 'not_yet_invited'
          ? 'Remove'
          : null;
    if (action === null) {
      return (
        <span className="personPage__topicState">
          {topic.started ? TOPIC_STATE_WORD[topic.state] : 'Not started'}
        </span>
      );
    }
    return (
      <button
        type="button"
        className="personPage__topicButton"
        disabled={busy || refreshing}
        onClick={() => changeTopics(topic)}
      >
        {action}
      </button>
    );
  };

  const sourceLine = (note: NoteView): JSX.Element => {
    const source = note.source;
    const noted = noteDate(note.at);
    if (source.kind === 'file') {
      return (
        <p className="decisionNote__source">
          From the file{' '}
          <Link to={`/plans/${planId}/files/${source.fileId}`}>{source.name}</Link>
          {noted === '' ? null : ` · ${noted}`}
        </p>
      );
    }
    const label =
      source.thread === 'shared'
        ? 'Everyone and Mor'
        : source.thread === 'principals'
          ? 'The principals and Mor'
          : 'You and Mor';
    const speaker =
      note.speaker === null ? null : note.speaker.you ? 'You' : note.speaker.name;
    const line = [speaker, label, source.topic, noted === '' ? null : noted]
      .filter((part): part is string => part !== null && part !== '')
      .join(' · ');
    return <p className="decisionNote__source">{line}</p>;
  };
  const section = (
    title: string,
    subtitle: string,
    items: readonly NoteView[],
    own: boolean,
  ): JSX.Element | null =>
    items.length === 0 ? null : (
      <section className="decisionNotes__section">
        <h2 className="decisionNotes__title">{title}</h2>
        <p className="decisionNotes__subtitle">{subtitle}</p>
        <div
          className={
            own ? 'decisionNotes__list decisionNotes__list--own' : 'decisionNotes__list'
          }
        >
          {items.map((note, index) => (
            <article className="decisionNote" key={index}>
              <p className="decisionNote__text">{note.text}</p>
              {sourceLine(note)}
            </article>
          ))}
        </div>
      </section>
    );

  const about = person.self ? 'About you' : `About ${first}`;
  const empty = notes.shared.length === 0 && notes.own.length === 0;
  const showTopics =
    canEdit && person.takesPart && notes.topics.some((topic) => topic.started);
  return (
    <section className="page decisionNotes personPage">
      <div className="personPage__head">
        <span
          className={`person__avatar personPage__avatar person__avatar--${hueOf(person.name)}`}
          aria-hidden="true"
        >
          {initialsOf(person.name)}
        </span>
        <div className="personPage__identity">
          <h1 className="page__title personPage__name">{shownName}</h1>
          {person.relation === null ? null : (
            <p className="personPage__relation">{relationLabel(person.relation)}</p>
          )}
        </div>
      </div>
      <p className="personPage__state">
        {emailLine === null ? stateLine : `${stateLine} ${emailLine}`}
      </p>
      {canRename || canEmail || canToggle || canDelete ? (
        <div className="personPage__actions">
          {canRename ? (
            <button
              type="button"
              className="personPage__action"
              disabled={busy}
              onClick={() => openForm('name')}
            >
              Rename
            </button>
          ) : null}
          {canEmail ? (
            <button
              type="button"
              className="personPage__action"
              disabled={busy}
              onClick={() => openForm('email')}
            >
              {hasEmail ? 'Change email' : 'Add email'}
            </button>
          ) : null}
          {canToggle ? (
            <button
              type="button"
              className="personPage__action"
              disabled={busy}
              onClick={() =>
                void send({
                  kind: 'set_person_takes_part',
                  person: person.id,
                  takesPart: !person.takesPart,
                })
              }
            >
              {person.takesPart
                ? `Remove ${first} as a collaborator on this plan`
                : `Add ${first} as a collaborator on this plan`}
            </button>
          ) : null}
          <span className="personPage__spacer" />
          {canDelete ? (
            <button
              type="button"
              className="personPage__delete"
              disabled={busy}
              onClick={() => openForm('delete')}
            >
              Delete
            </button>
          ) : null}
        </div>
      ) : null}
      {editing === 'name' || editing === 'email' ? (
        <InlineForm
          label={editing === 'name' ? 'Name' : 'Email'}
          email={editing === 'email'}
          initial={editing === 'name' ? person.name : (person.email ?? '')}
          busy={busy}
          error={problem}
          onCancel={closeForm}
          onSave={async (value) => {
            const saved = await send(
              editing === 'name'
                ? { kind: 'rename_person', person: person.id, name: value }
                : { kind: 'set_person_email', person: person.id, email: value },
            );
            if (saved) {
              setEditing(null);
            }
          }}
        />
      ) : null}
      {editing === 'delete' ? (
        <div className="personPage__confirm">
          <RemoveConfirm
            question={`Delete ${first} from the list?`}
            actionLabel="Delete"
            busy={busy}
            error={problem}
            onCancel={closeForm}
            onRemove={() => {
              void send({ kind: 'remove_person', person: person.id }).then((removed) => {
                if (removed) {
                  navigate(`/plans/${planId}/people`);
                }
              });
            }}
          />
        </div>
      ) : null}
      {problem === null || editing !== null ? null : (
        <p className="screen__problem">{problem}</p>
      )}
      {showTopics ? (
        <section className="personPage__topics">
          <h2 className="personPage__topicsTitle">Topics</h2>
          <ul className="personPage__topicList">
            {notes.topics
              .filter((topic) => topic.started)
              .map((topic) => (
                <li className="personPage__topic" key={topic.key}>
                  <span>{topic.label}</span>
                  <span className="personPage__topicEnd">{topicEnd(topic)}</span>
                </li>
              ))}
          </ul>
        </section>
      ) : null}
      {section(
        'What has been said',
        `${about}, from conversations you’re in and from files you can see.`,
        notes.shared,
        false,
      )}
      {section(
        'What you’ve told Mor privately',
        `${about}, from your private conversation with Mor.`,
        notes.own,
        true,
      )}
      {empty ? (
        <p className="decisionNotes__empty">
          Nothing has been said about {person.self ? 'you' : first} yet.
        </p>
      ) : null}
    </section>
  );
}
